import { getLogger } from '@kit/logger';

import { NullAnalyticsService } from './null-analytics-service';
import type {
  AnalyticsManager,
  AnalyticsService,
  CreateAnalyticsManagerOptions,
} from './types';

export function createAnalyticsManager<T extends string, Config extends object>(
  options: CreateAnalyticsManagerOptions<T, Config>,
): AnalyticsManager {
  const activeServices = new Map<T, AnalyticsService>();

  const getActiveServices = (): AnalyticsService[] => {
    if (activeServices.size === 0) {
      void getLogger().then((logger) =>
        logger.debug(
          { service: 'analytics', function: 'getActiveServices' },
          'No active analytics services. Using NullAnalyticsService.',
        ),
      );

      return [NullAnalyticsService];
    }

    return Array.from(activeServices.values());
  };

  const registerActiveServices = (
    options: CreateAnalyticsManagerOptions<T, Config>,
  ) => {
    Object.keys(options.providers).forEach((provider) => {
      const providerKey = provider as keyof typeof options.providers;
      const factory = options.providers[providerKey];

      if (!factory) {
        void getLogger().then((logger) =>
          logger.warn(
            {
              service: 'analytics',
              function: 'registerActiveServices',
              provider,
            },
            `Analytics provider '${provider}' not registered. Skipping initialization.`,
          ),
        );

        return;
      }

      const service = factory();
      activeServices.set(provider as T, service);

      void service.initialize();
    });
  };

  registerActiveServices(options);

  return {
    identify: (userId: string, traits?: Record<string, string>) => {
      return Promise.all(
        getActiveServices().map((service) => service.identify(userId, traits)),
      );
    },

    trackPageView: (path: string) => {
      return Promise.all(
        getActiveServices().map((service) => service.trackPageView(path)),
      );
    },

    trackEvent: (
      eventName: string,
      eventProperties?: Record<string, string | string[]>,
    ) => {
      return Promise.all(
        getActiveServices().map((service) =>
          service.trackEvent(eventName, eventProperties),
        ),
      );
    },
  };
}
